/**
 * BILLING TYPES
 * 
 * Simple, clear pricing model with monthly limits for each feature.
 * No abstract "credits" — each feature has its own clear limit.
 */

// =============================================================================
// SUBSCRIPTION TIERS
// =============================================================================

export type SubscriptionTier = 
  | 'free'           // $0 — default for new accounts (1 watermarked trial listing)
  | 'starter'        // $30/mo
  | 'growth'         // $90/mo
  | 'studio'         // $300/mo — displayed as "Team"
  | 'one_off_kit';   // $15 one-time

// =============================================================================
// SERVICE TYPES (for tracking usage)
// =============================================================================

export type CreditServiceType =
  | 'PHOTO_EDIT'             // Photo Enhancement & Cleanup (1 per photo)
  | 'VIRTUAL_STAGING'        // Virtual Staging (1 per property)
  | 'AI_ADDITION'            // AI Additions - add pools, patios, etc. (1 per edit)
  | 'REEDIT'                 // Post-creation photo re-edits
  | 'VIDEO_GENERATION'       // Video slideshow (free)
  | 'SUBSCRIPTION_GRANT'     // Monthly allocation
  | 'ONE_OFF_KIT_GRANT'      // One-off kit allocation
  | 'ADMIN_ADJUSTMENT';      // Manual admin adjustment

export type ListingStatus = 'active' | 'archived' | 'cold_storage';

// =============================================================================
// SERVICE DISPLAY NAMES
// =============================================================================

export const SERVICE_DISPLAY_NAMES: Record<string, string> = {
  PHOTO_EDIT: 'Photo Edit',
  VIRTUAL_STAGING: 'Virtual Staging',
  AI_ADDITION: 'AI Addition',
  REEDIT: 'Re-edit',
  VIDEO_GENERATION: 'Video Generation',
  SUBSCRIPTION_GRANT: 'Monthly Allocation',
  ONE_OFF_KIT_GRANT: 'One-Off Kit Allocation',
  ADMIN_ADJUSTMENT: 'Admin Adjustment',
  // Legacy mappings for backwards compatibility
  P1_ENHANCEMENT: 'Photo Edit',
  P2_CLEANUP: 'Photo Edit',
  P3_STAGING: 'Virtual Staging',
  P4_GEN_FILL: 'AI Addition',
};

export function getServiceDisplayName(serviceType: string): string {
  return SERVICE_DISPLAY_NAMES[serviceType] || serviceType;
}

// =============================================================================
// TIER CONFIGURATION
// =============================================================================

export interface TierConfig {
  name: string;
  displayName: string;
  priceCents: number;
  annualPriceCents: number | null;
  activeListingsLimit: number | null; // null = unlimited
  maxUsers: number; // Team members allowed
  monthlyLimits: {
    photoEdits: number | null;        // null = unlimited (not used currently)
    virtualStagingProperties: number; // Per property, not per photo
    aiAdditions: number;              // Individual AI edits
    videos: number;                   // Per property (same as property limit)
    reedits: number;                  // Monthly pool across all listings/photos
  };
  features: string[];
  // Feature access flags
  hasPreviewMicrosite: boolean; // Access to shareable preview listing microsite
  hasListingScoreCard: boolean; // Access to AI listing quality score card
  hasPremiumDescription: boolean; // Access to conversion-optimized premium descriptions
  // For backwards compatibility with credit system
  monthlyCredits: number;
}

// Trial limits for new users who haven't subscribed yet
// They get 1 free listing with limited photo edits (watermarked)
export const TRIAL_LIMITS = {
  photoEdits: 10,            // 10 free photo edits for trial
  aiAdditions: 5,            // 5 free AI additions for trial
  virtualStagingProperties: 1, // 1 free staging for trial
  activeListingsLimit: 1,    // 1 trial listing
};

export const TIER_CONFIGS: Record<SubscriptionTier, TierConfig> = {
  free: {
    name: 'free',
    displayName: 'Free trial',
    priceCents: 0,
    annualPriceCents: null,
    activeListingsLimit: TRIAL_LIMITS.activeListingsLimit,
    maxUsers: 1,
    monthlyLimits: {
      photoEdits: TRIAL_LIMITS.photoEdits,
      virtualStagingProperties: TRIAL_LIMITS.virtualStagingProperties,
      aiAdditions: TRIAL_LIMITS.aiAdditions,
      videos: 1,
      reedits: 2,
    },
    features: [
      '1 free property listing',
      '10 AI-enhanced photos (watermarked)',
      'Virtual staging & AI additions included',
      'Descriptions & social posts (preview only)',
      'Video slideshow & shareable microsite',
      'Upgrade any time to remove watermarks',
    ],
    hasPreviewMicrosite: true,
    hasListingScoreCard: false,
    hasPremiumDescription: true,
    monthlyCredits: 0,
  },
  starter: {
    name: 'starter',
    displayName: 'Starter',
    priceCents: 3000, // $30/month
    annualPriceCents: 2500, // $300/year = $25/month
    activeListingsLimit: 5,
    maxUsers: 1,
    monthlyLimits: {
      photoEdits: 50,
      virtualStagingProperties: 5,
      aiAdditions: 50, // Same as photo edits
      videos: 5, // 1 per property (same as property limit)
      reedits: 10,
    },
    features: [
      'Manage up to 5 properties',
      '50 photo edits per month',
      '10 monthly re-edits across all listings',
      'Stage 5 properties per month',
      '50 AI additions per month',
      'Unlimited videos',
      '28 language translations',
    ],
    hasPreviewMicrosite: false, // Not available for starter
    hasListingScoreCard: false,
    hasPremiumDescription: false,
    monthlyCredits: 75,
  },
  growth: {
    name: 'growth',
    displayName: 'Growth',
    priceCents: 9000, // $90/month
    annualPriceCents: 7500, // $900/year = $75/month
    activeListingsLimit: 15,
    maxUsers: 3,
    monthlyLimits: {
      photoEdits: 150,
      virtualStagingProperties: 15, // Same as property limit
      aiAdditions: 150, // Same as photo edits
      videos: 15, // 1 per property (same as property limit)
      reedits: 30,
    },
    features: [
      'Manage up to 15 properties',
      '150 photo edits per month',
      '30 monthly re-edits across all listings',
      'Stage 15 properties per month',
      '150 AI additions per month',
      'Unlimited videos',
      '28 language translations',
      'Up to 3 team members',
      'Shareable listing microsites',
    ],
    hasPreviewMicrosite: true, // Available for growth
    hasListingScoreCard: false,
    hasPremiumDescription: true,
    monthlyCredits: 250,
  },
  studio: {
    name: 'studio',
    displayName: 'Team',
    priceCents: 30000, // $300/month
    annualPriceCents: 25000, // $3000/year = $250/month
    activeListingsLimit: 50, // 50 properties
    maxUsers: 10,
    monthlyLimits: {
      photoEdits: 500,
      virtualStagingProperties: 50, // Same as property limit
      aiAdditions: 500, // Same as photo edits
      videos: 50, // 1 per property (same as property limit)
      reedits: 100,
    },
    features: [
      'Manage up to 50 properties',
      '500 photo edits per month',
      '100 monthly re-edits across all listings',
      'Stage 50 properties per month',
      '500 AI additions per month',
      'Unlimited videos',
      'Up to 10 team members',
      'Shareable listing microsites',
      'AI Listing Score Card',
      'API access',
      'Dedicated support',
    ],
    hasPreviewMicrosite: true, // Available for studio
    hasListingScoreCard: true, // Only available for studio
    hasPremiumDescription: true,
    monthlyCredits: 1000,
  },
  one_off_kit: {
    name: 'one_off_kit',
    displayName: 'Single Property Kit',
    priceCents: 1500, // $15 one-time
    annualPriceCents: null,
    activeListingsLimit: 1,
    maxUsers: 1,
    monthlyLimits: {
      photoEdits: 10,
      virtualStagingProperties: 1,
      aiAdditions: 10,
      videos: 1,
      reedits: 5,
    },
    features: [
      '1 property listing',
      '10 AI-enhanced photos (upgrade to 20 for $5)',
      '5 included re-edits for this property',
      'Virtual staging included',
      'AI additions (pools, patios & more)',
      'Premium AI descriptions',
      'Multi-platform descriptions & social posts',
      '28+ language translations',
      'Shareable microsite & QR code',
      'PDF flyer & video slideshow',
      'No subscription required',
    ],
    hasPreviewMicrosite: true,
    hasListingScoreCard: false,
    hasPremiumDescription: true,
    monthlyCredits: 30,
  },
};

// =============================================================================
// USER BILLING STATE
// =============================================================================

export interface UserBillingState {
  userId: string;
  subscriptionTier: SubscriptionTier;
  creditBalance: number; // Legacy, kept for backwards compatibility
  activeListingsCount: number;
  /**
   * Lifetime number of listings this account owns (from profiles.active_listings_count,
   * kept in sync by a DB trigger). Used for trial eligibility, which must not reset
   * when the rolling 30-day usage window rolls over.
   */
  lifetimeListingsCount?: number;
  activeListingsLimit: number | null;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  subscriptionStatus: 'active' | 'canceled' | 'past_due' | 'inactive';
  subscriptionPeriodStart?: Date;
  subscriptionPeriodEnd?: Date;
  monthlyCreditsGranted: number; // Legacy
  // New usage tracking
  monthlyPhotoEditsUsed?: number;
  monthlyStagingPropertiesUsed?: number;
  monthlyAiAdditionsUsed?: number;
  monthlyVideosUsed?: number;
  monthlyReeditsUsed?: number;
  usagePeriodStart?: Date;
  // One-off kit
  oneOffKitPurchased: boolean;
  oneOffKitPhotosRemaining?: number;
  oneOffKitVideosRemaining?: number;
}

// =============================================================================
// CREDIT TRANSACTION (Legacy, kept for backwards compatibility)
// =============================================================================

export interface CreditTransaction {
  id: string;
  userId: string;
  serviceType: CreditServiceType;
  creditAmount: number;
  balanceAfter: number;
  listingId?: string;
  photoIndex?: number;
  description?: string;
  metadata?: Record<string, unknown>;
  createdAt: Date;
}

// =============================================================================
// ENHANCEMENT FEATURE DEFINITIONS (for UI)
// =============================================================================

export type EnhancementFeatureType = 
  | 'PHOTO_EDIT'
  | 'VIRTUAL_STAGING'
  | 'AI_ADDITION';

export interface EnhancementFeatureDefinition {
  id: EnhancementFeatureType;
  name: string;
  description: string;
  whatItDoes: string[];
  pricingNote: string;
  icon: string;
}

export const ENHANCEMENT_FEATURES: EnhancementFeatureDefinition[] = [
  {
    id: 'PHOTO_EDIT',
    name: 'Photo Editing',
    description: 'Professional photo enhancement',
    whatItDoes: [
      'Brightness & exposure correction',
      'HDR & contrast optimization',
      'Color correction',
      'Object removal & cleanup',
      'Sky replacement',
    ],
    pricingNote: 'Counted per photo',
    icon: 'Sparkles',
  },
  {
    id: 'VIRTUAL_STAGING',
    name: 'Virtual Staging',
    description: 'Add decor to make spaces feel welcoming',
    whatItDoes: [
      'Small decorative items (plants, vases, pillows)',
      'Applied to all photos in your property',
      'Makes empty spaces feel lived-in',
    ],
    pricingNote: 'Counted per property (all photos)',
    icon: 'Sofa',
  },
  {
    id: 'AI_ADDITION',
    name: 'AI Additions',
    description: 'Add pools, patios, landscaping & more',
    whatItDoes: [
      'Add swimming pools',
      'Add patios & outdoor living spaces',
      'Landscaping improvements',
      'Custom AI edits via prompts',
    ],
    pricingNote: 'Counted per edit',
    icon: 'Wand2',
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function canCreateListing(billing: UserBillingState): boolean {
  // Use tier config as authoritative source for limits (not database value which may be stale)
  const tierConfig = TIER_CONFIGS[billing.subscriptionTier] || TIER_CONFIGS['free'];
  const limit = tierConfig?.activeListingsLimit ?? null;
  
  if (limit === null) return true;
  return billing.activeListingsCount < limit;
}

export function formatCredits(credits: number): string {
  if (credits >= 1000) {
    return `${(credits / 1000).toFixed(1)}k`;
  }
  return credits.toString();
}

export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

export function formatPriceDecimal(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Check if user has remaining quota for a feature
 */
export function hasRemainingQuota(
  billing: UserBillingState,
  feature: 'photoEdits' | 'virtualStagingProperties' | 'aiAdditions' | 'videos' | 'reedits'
): boolean {
  const tierConfig = TIER_CONFIGS[billing.subscriptionTier] || TIER_CONFIGS['free'];
  const limit = tierConfig?.monthlyLimits?.[feature] ?? 0;
  
  if (limit === 0) return false; // Not included
  
  switch (feature) {
    case 'photoEdits':
      return (billing.monthlyPhotoEditsUsed || 0) < limit;
    case 'virtualStagingProperties':
      return (billing.monthlyStagingPropertiesUsed || 0) < limit;
    case 'aiAdditions':
      return (billing.monthlyAiAdditionsUsed || 0) < limit;
    case 'videos':
      return (billing.monthlyVideosUsed || 0) < limit;
    case 'reedits':
      return (billing.monthlyReeditsUsed || 0) < limit;
    default:
      return false;
  }
}

/**
 * Check if user is on the free trial.
 * Prefer `isTrialUser` from `@/lib/trialUtils`, which also accounts for listing counts.
 */
export function isTrialUser(billing: UserBillingState): boolean {
  if (billing.subscriptionTier === 'free') return !billing.oneOffKitPurchased;
  // Legacy: unpaid accounts used to sit on the one_off_kit tier
  return billing.subscriptionTier === 'one_off_kit' && !billing.oneOffKitPurchased;
}

/**
 * Get remaining quota for a feature
 */
export function getRemainingQuota(
  billing: UserBillingState,
  feature: 'photoEdits' | 'virtualStagingProperties' | 'aiAdditions' | 'videos' | 'reedits'
): number {
  const tierConfig = TIER_CONFIGS[billing.subscriptionTier] || TIER_CONFIGS['free'];
  const limit = tierConfig?.monthlyLimits?.[feature] ?? 0;
  
  if (limit === 0) return 0;
  
  switch (feature) {
    case 'photoEdits':
      return Math.max(0, limit - (billing.monthlyPhotoEditsUsed || 0));
    case 'virtualStagingProperties':
      return Math.max(0, limit - (billing.monthlyStagingPropertiesUsed || 0));
    case 'aiAdditions':
      return Math.max(0, limit - (billing.monthlyAiAdditionsUsed || 0));
    case 'videos':
      return Math.max(0, limit - (billing.monthlyVideosUsed || 0));
    case 'reedits':
      return Math.max(0, limit - (billing.monthlyReeditsUsed || 0));
    default:
      return 0;
  }
}

/**
 * Check if user has access to shareable preview microsites
 * Only available for Growth and Studio subscribers
 */
export function hasPreviewMicrositeAccess(billing: UserBillingState): boolean {
  const tierConfig = TIER_CONFIGS[billing.subscriptionTier] || TIER_CONFIGS['free'];
  return tierConfig?.hasPreviewMicrosite ?? false;
}

/**
 * Check if user has access to shareable preview microsites by tier name
 */
export function tierHasPreviewMicrositeAccess(tier: SubscriptionTier): boolean {
  const tierConfig = TIER_CONFIGS[tier] || TIER_CONFIGS['free'];
  return tierConfig?.hasPreviewMicrosite ?? false;
}

/**
 * Check if user has access to AI Listing Score Card
 * Only available for Studio subscribers
 */
export function hasListingScoreCardAccess(billing: UserBillingState): boolean {
  const tierConfig = TIER_CONFIGS[billing.subscriptionTier] || TIER_CONFIGS['free'];
  return tierConfig?.hasListingScoreCard ?? false;
}

/**
 * Check if a tier has listing score card access
 */
export function tierHasListingScoreCardAccess(tier: SubscriptionTier): boolean {
  const tierConfig = TIER_CONFIGS[tier] || TIER_CONFIGS['free'];
  return tierConfig?.hasListingScoreCard ?? false;
}

/**
 * Check if user has access to Premium Description
 * Only available for Growth and Studio subscribers
 */
export function hasPremiumDescriptionAccess(billing: UserBillingState): boolean {
  const tierConfig = TIER_CONFIGS[billing.subscriptionTier] || TIER_CONFIGS['free'];
  return tierConfig?.hasPremiumDescription ?? false;
}

/**
 * Check if a tier has premium description access
 */
export function tierHasPremiumDescriptionAccess(tier: SubscriptionTier): boolean {
  const tierConfig = TIER_CONFIGS[tier] || TIER_CONFIGS['free'];
  return tierConfig?.hasPremiumDescription ?? false;
}
